import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { nonce as runtimeNonce, shopItemsApiUrl, shopPurchaseApiUrl } from '../utils/runtime';
import { acceptHarborOrder, claimActiveHarborOrder, loadHarborOrders } from '../api/harborOrdersApi';
import { useLoot } from '../hooks/useLoot';
import { useGamification } from '../hooks/GamificationContext';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { useOptionalGameAudio } from '../game/audio/GameAudioContext';
import harborImage from '../assets/freibeuterhafen.png';
import fundWorkshopSign from '../assets/harbor/fundwerkstatt-wegweiser.png';
import harborAmbienceUrl from '../assets/sounds/game/meeresrauschen.mp3';

const HARBOR_VIDEO_URL = 'https://app.pfandpirat.com/wp-content/uploads/freibeuterhafen.mp4';

const MARKET_SLUGS = ['energy_drink', 'xp_boost', 'old_bottle_message', 'shipyard_wood_planks', 'shipyard_rope', 'shipyard_nail_crate', 'shipyard_sailcloth', 'shipyard_resin', 'shipyard_iron_fittings'];

const FALLBACK_MARKET_ITEMS = {
  energy_drink: {
    slug: 'energy_drink',
    title: 'Energydrink',
    description: 'Bringt dich im Schrittzähler um 100 Schritte weiter und hält deine Expeditionen am Laufen.',
    category: 'market',
    price_bronze: 50,
    price_label: '1 Silbermünze',
    effect_type: 'inventory_item',
    effect_value: 'Energydrink',
    daily_limit: 3,
    daily_remaining: null,
    icon: '⚡',
    fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/Energydrink.png',
    tag: 'Schritt-Boost',
  },
  xp_boost: {
    slug: 'xp_boost',
    title: 'XP-Boost',
    description: 'Sichert dir beim Benutzen zusätzliche Erfahrungspunkte für deinen Piratenfortschritt.',
    category: 'market',
    price_bronze: 100,
    price_label: '1 Goldmünze',
    effect_type: 'inventory_item',
    effect_value: 'XP-Boost',
    daily_limit: 1,
    daily_remaining: null,
    icon: '⚡',
    fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/XP-Boost.png',
    tag: 'XP-Vorteil',
  },
  old_bottle_message: {
    slug: 'old_bottle_message',
    title: 'Alte Flaschenpost',
    description: 'Hinterlasse später eine Nachricht an deinem aktuellen Ort und baue eigene kleine Loot-Orte auf.',
    category: 'market',
    price_bronze: 200,
    price_label: '2 Goldmünzen',
    effect_type: 'inventory_item',
    effect_value: 'Alte Flaschenpost',
    daily_limit: 1,
    daily_remaining: null,
    icon: '🍾',
    fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/Alte-Flaschenpost.png',
    tag: 'Orts-Item',
  },
  shipyard_wood_planks: { slug: 'shipyard_wood_planks', title: 'Holzplanken', description: 'Stabile Holzplanken für dein nächstes Werft-Upgrade.', category: 'market', price_bronze: 5, price_label: '5 Bronzemünzen', effect_type: 'shipyard_resource', effect_value: 'wood_planks', daily_limit: 10, daily_remaining: null, icon: '🪵', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/holzplanken.png', tag: 'Werft-Ressource' },
  shipyard_rope: { slug: 'shipyard_rope', title: 'Seile', description: 'Robustes Seemannsgarn für Takelage, Mast und Ladung.', category: 'market', price_bronze: 8, price_label: '8 Bronzemünzen', effect_type: 'shipyard_resource', effect_value: 'rope', daily_limit: 8, daily_remaining: null, icon: '🪢', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif', tag: 'Werft-Ressource' },
  shipyard_nail_crate: { slug: 'shipyard_nail_crate', title: 'Nagelkiste', description: 'Nägel und Kleinteile für stabile Schiffsbauten.', category: 'market', price_bronze: 10, price_label: '10 Bronzemünzen', effect_type: 'shipyard_resource', effect_value: 'nail_crate', daily_limit: 6, daily_remaining: null, icon: '📦', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/nagelkiste.png', tag: 'Werft-Ressource' },
  shipyard_sailcloth: { slug: 'shipyard_sailcloth', title: 'Segeltuch', description: 'Festes Segeltuch für mehr Fahrt und bessere Manöver.', category: 'market', price_bronze: 50, price_label: '1 Silbermünze', effect_type: 'shipyard_resource', effect_value: 'sailcloth', daily_limit: 3, daily_remaining: null, icon: '⛵', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/segeltuch.png', tag: 'Werft-Ressource' },
  shipyard_resin: { slug: 'shipyard_resin', title: 'Harz', description: 'Klebriges Harz zum Abdichten alter Planken.', category: 'market', price_bronze: 75, price_label: '1 Silber · 25 Bronze', effect_type: 'shipyard_resource', effect_value: 'resin', daily_limit: 2, daily_remaining: null, icon: '🍯', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/harz.png', tag: 'Werft-Ressource' },
  shipyard_iron_fittings: { slug: 'shipyard_iron_fittings', title: 'Eisenbeschläge', description: 'Schwere Beschläge für Rumpf, Anker und Kanonenhalterungen.', category: 'market', price_bronze: 100, price_label: '2 Silbermünzen', effect_type: 'shipyard_resource', effect_value: 'iron_fittings', daily_limit: 2, daily_remaining: null, icon: '🔩', fallback_image: 'https://app.pfandpirat.com/wp-content/uploads/eisenbeschlaege.png', tag: 'Werft-Ressource' },
};

const HARBOR_AREAS = [
  { key: 'market', label: 'Markt', icon: '🧰', x: 14, y: 42, active: true, teaser: 'Jetzt geöffnet', text: 'Hier kaufst du nützliche Game-Items gegen Münzen.' },
  { key: 'tavern', label: 'Taverne', icon: '🍺', x: 40, y: 39, active: true, teaser: 'aktiv', text: 'Persönlicher Spielraum für Quiz, Aufträge und faire Tagesbelohnungen.' },
  { key: 'harbormaster', label: 'Hafenmeister', icon: '⚓', x: 66, y: 45, active: true, teaser: 'Hafen-Duelle', text: 'Melde dein Schiff zu Duellen und Hafen-Events an.' },
  { key: 'shipyard', label: 'Werft', icon: '⚓', x: 89, y: 45, active: true, teaser: 'aktiv', text: 'Baue dein eigenes Schiff und schalte später Vorteile frei.' },
  { key: 'fundworkshop', label: 'Fundwerkstatt', icon: '♻️', x: 21, y: 81, active: true, teaser: 'neu', stateLabel: 'neu', text: 'Verwerte häufige Fundstücke zu nützlichen Materialien und normaler XP.' },
  { key: 'quests', label: 'Aufträge', icon: '📜', x: 66, y: 72, active: true, teaser: 'Jetzt geöffnet', text: 'Wähle einen aktiven Auftrag, der die alte Tagesaufgabe in der Übersicht ersetzt.' },
];

function normalizeLootKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&[a-z0-9#]+;/gi, '')
    .replace(/[^a-z0-9]+/g, '');
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, '').trim();
}

function getItemImageFromLoot(loot, title, effectValue) {
  const wantedKeys = [title, effectValue].map(normalizeLootKey).filter(Boolean);
  const lootItems = Array.isArray(loot) ? loot : [];

  const found = lootItems.find((item) => {
    const itemTitle = stripHtml(item?.title?.rendered || item?.title || '');
    const extra = item?.pfandpirat_loot_meta || {};
    return wantedKeys.some((key) => (
      normalizeLootKey(itemTitle) === key ||
      normalizeLootKey(item?.slug || '') === key ||
      normalizeLootKey(extra.expeditionKey || extra.expedition_key || '') === key
    ));
  });

  if (!found) return '';

  const embeddedImage = found?._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium?.source_url
    || found?._embedded?.['wp:featuredmedia']?.[0]?.source_url
    || '';
  const extra = found?.pfandpirat_loot_meta || {};

  return embeddedImage || extra.imageUrl || extra.image_url || '';
}

function getCsrfHeaders(nonce) {
  return {
    'Content-Type': 'application/json',
    ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
  };
}

async function readJsonResponse(response) {
  const raw = await response.text();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    throw new Error('Server-Antwort ist kein sauberes JSON.');
  }
}

function formatWallet(wallet) {
  return wallet?.balance?.short_label || wallet?.balance?.label || '0 Gold · 0 Silber · 0 Bronze';
}

function getWalletBronze(wallet) {
  return Number(wallet?.balance?.bronze || 0);
}

function formatInternalBronze(value) {
  const normalized = Math.max(0, Number(value || 0));
  return `${normalized} Bronze intern`;
}

function formatOrderReward(order) {
  const xp = Number(order?.rewards?.xp || 0);
  const bronze = order?.rewards?.bronze_label || '';
  const parts = [];
  if (xp > 0) parts.push(`+${xp} XP`);
  if (bronze) parts.push(bronze);
  return parts.join(' · ') || 'Belohnung offen';
}

function formatOrderProgress(order) {
  const progress = order?.progress || {};
  if (progress.label) return progress.label;
  const current = Number(progress.current || 0);
  const target = Number(progress.target || order?.target_value || 0);
  return `${current} / ${target}`;
}

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function isNoActiveOrderError(error) {
  const code = error?.payload?.code || error?.payload?.data?.code || '';
  return error?.status === 404 || code === 'harbor_no_active_order';
}

function limitLabel(item) {
  const dailyLimit = Number(item?.daily_limit || 0);
  const weeklyLimit = Number(item?.weekly_limit || 0);
  const dailyRemaining = item?.daily_remaining;
  const weeklyRemaining = item?.weekly_remaining;

  if (dailyLimit > 0 && dailyRemaining !== null && dailyRemaining !== undefined) {
    const remaining = Math.max(0, Number(dailyRemaining || 0));
    return `Heute noch ${remaining}/${dailyLimit} kaufbar`;
  }

  if (weeklyLimit > 0 && weeklyRemaining !== null && weeklyRemaining !== undefined) {
    const remaining = Math.max(0, Number(weeklyRemaining || 0));
    return `Diese Woche noch ${remaining}/${weeklyLimit} kaufbar`;
  }

  if (dailyLimit > 0) return `${dailyLimit}x täglich`;
  if (weeklyLimit > 0) return `${weeklyLimit}x pro Woche`;

  return 'Solange Vorrat reicht';
}

export default function FreibeuterhafenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const nonce = window?.pfandpiratData?.nonce || runtimeNonce;
  const loot = useLoot();
  const { applyGamificationPayload } = useGamification() || {};
  const sessionContext = useOptionalAppSession();
  const gameAudio = useOptionalGameAudio();
  const bootstrapWallet = sessionContext?.session?.wallet?.wallet || sessionContext?.session?.wallet || null;
  const bootstrapHarborOrders = sessionContext?.session?.harbor_orders || null;
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const popupTimerRef = useRef(null);
  const flashTimerRef = useRef(null);
  const walletPulseTimerRef = useRef(null);
  const lockShakeTimerRef = useRef(null);
  const marketSectionRef = useRef(null);
  const ordersSectionRef = useRef(null);

  const [wallet, setWallet] = useState(() => bootstrapWallet);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState(() => (Array.isArray(bootstrapHarborOrders?.orders) ? bootstrapHarborOrders.orders : []));
  const [activeOrder, setActiveOrder] = useState(() => bootstrapHarborOrders?.active_order || null);
  const [ordersLoading, setOrdersLoading] = useState(!bootstrapHarborOrders);
  const [ordersError, setOrdersError] = useState(null);
  const [orderActionId, setOrderActionId] = useState(null);
  const [activeArea, setActiveArea] = useState('market');
  const [notice, setNotice] = useState(null);
  const [purchaseState, setPurchaseState] = useState(null);
  const [purchaseFlashSlug, setPurchaseFlashSlug] = useState(null);
  const [walletPulse, setWalletPulse] = useState(false);
  const [shakenArea, setShakenArea] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [failedItemImages, setFailedItemImages] = useState({});
  const prefersReducedMotion = useMemo(() => (
    typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ), []);

  const showNotice = useCallback((nextNotice, duration = 3600) => {
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
    }
    setNotice(nextNotice);
    popupTimerRef.current = window.setTimeout(() => {
      setNotice(null);
      popupTimerRef.current = null;
    }, duration);
  }, [mergeSessionPatch]);

  const closeNotice = useCallback(() => {
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }
    setNotice(null);
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      setOrdersError(null);
      const payload = await loadHarborOrders();
      const nextOrders = Array.isArray(payload?.orders) ? payload.orders : [];
      const nextActiveOrder = payload?.active_order || null;
      setOrders(nextOrders);
      setActiveOrder(nextActiveOrder);
      if (mergeSessionPatch) {
        mergeSessionPatch({ harbor_orders: { ...(payload || {}), orders: nextOrders, active_order: nextActiveOrder } });
      }
    } catch (loadError) {
      setOrdersError(loadError);
    } finally {
      setOrdersLoading(false);
    }
  }, [mergeSessionPatch]);

  const loadShop = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(shopItemsApiUrl || '/wp-json/pfandpirat/v1/shop/items', {
        credentials: 'include',
        headers: nonce ? { 'X-WP-Nonce': nonce } : {},
      });
      const payload = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(payload?.message || 'Der Freibeuterhafen konnte nicht geladen werden.');
      }

      const nextWallet = payload?.wallet || null;
      setWallet(nextWallet);
      if (payload?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(payload.session_patch);
      } else if (mergeSessionPatch) {
        mergeSessionPatch({ wallet: { wallet: nextWallet } });
      }
      setItems(Array.isArray(payload?.items) ? payload.items : []);
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [mergeSessionPatch, nonce]);

  useEffect(() => {
    loadShop();
    if (bootstrapHarborOrders) {
      setOrders(Array.isArray(bootstrapHarborOrders.orders) ? bootstrapHarborOrders.orders : []);
      setActiveOrder(bootstrapHarborOrders.active_order || null);
      setOrdersLoading(false);
    } else {
      loadOrders();
    }

    const handleOrderClaimed = () => loadOrders();
    window.addEventListener('pfandpirat:harbor-order-claimed', handleOrderClaimed);

    return () => {
      window.removeEventListener('pfandpirat:harbor-order-claimed', handleOrderClaimed);
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (walletPulseTimerRef.current) clearTimeout(walletPulseTimerRef.current);
      if (lockShakeTimerRef.current) clearTimeout(lockShakeTimerRef.current);
      gameAudio?.stopLoop('harbor-ambience', 350);
    };
  }, [bootstrapHarborOrders, gameAudio, loadOrders, loadShop]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    if (searchParams.get('focus') !== 'orders') return undefined;

    const timer = window.setTimeout(() => {
      ordersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveArea('quests');
    }, 180);

    return () => window.clearTimeout(timer);
  }, [location.search, ordersLoading]);

  const marketItems = useMemo(() => {
    const apiItems = new Map();
    for (const item of items) {
      if (MARKET_SLUGS.includes(item?.slug)) {
        apiItems.set(item.slug, item);
      }
    }

    return MARKET_SLUGS.map((slug) => {
      const base = FALLBACK_MARKET_ITEMS[slug];
      const apiItem = apiItems.get(slug) || {};
      const merged = { ...base, ...apiItem };
      const lootImage = getItemImageFromLoot(loot, merged.title, merged.effect_value);
      return {
        ...merged,
        image: merged.icon_url || lootImage || merged.fallback_image || '',
        tag: base.tag,
      };
    });
  }, [items, loot]);

  const triggerWalletPulse = useCallback(() => {
    if (walletPulseTimerRef.current) clearTimeout(walletPulseTimerRef.current);
    setWalletPulse(true);
    walletPulseTimerRef.current = window.setTimeout(() => {
      setWalletPulse(false);
      walletPulseTimerRef.current = null;
    }, 1100);
  }, []);

  const triggerPurchaseFlash = useCallback((slug) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setPurchaseFlashSlug(slug);
    flashTimerRef.current = window.setTimeout(() => {
      setPurchaseFlashSlug(null);
      flashTimerRef.current = null;
    }, 1300);
  }, []);

  const triggerLockShake = useCallback((areaKey) => {
    if (lockShakeTimerRef.current) clearTimeout(lockShakeTimerRef.current);
    setShakenArea(areaKey);
    lockShakeTimerRef.current = window.setTimeout(() => {
      setShakenArea(null);
      lockShakeTimerRef.current = null;
    }, 720);
  }, []);

  const toggleHarborSound = useCallback(async () => {
    if (soundEnabled) {
      gameAudio?.stopLoop('harbor-ambience', 400);
      setSoundEnabled(false);
      return;
    }

    try {
      if (!gameAudio) throw new Error('Game audio unavailable');
      await gameAudio.unlock();
      await gameAudio.playLoop('harbor-ambience', harborAmbienceUrl, { bus: 'ambience', volume: 0.24, fadeMs: 500 });
      setSoundEnabled(true);
    } catch (soundError) {
      showNotice({
        type: 'error',
        icon: '🔇',
        title: 'Hafensound konnte nicht gestartet werden',
        badge: 'Sound blockiert',
        lines: ['Der Browser hat die Wiedergabe verhindert. Bitte tippe den Sound-Button erneut an.'],
      }, 3600);
    }
  }, [gameAudio, showNotice, soundEnabled]);

  const handleSetSail = useCallback(() => {
    gameAudio?.stopLoop('harbor-ambience', 450);
    gameAudio?.playTone('harbor-depart', { bus: 'ui', frequency: 180, endFrequency: 420, duration: 0.32, volume: 0.2, cooldownMs: 1000 });
    navigate('/ship', { state: { departureFromHarbor: true } });
  }, [gameAudio, navigate]);

  const handleAreaClick = (area) => {
    setActiveArea(area.key);

    if (area.active) {
      if (area.key === 'market') {
        navigate('/freibeuterhafen');
        window.requestAnimationFrame(() => {
          marketSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }

      if (area.key === 'quests') {
        navigate('/freibeuterhafen?focus=orders');
        window.requestAnimationFrame(() => {
          ordersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }

      const routeMap = {
        tavern: '/freibeuterhafen/taverne',
        harbormaster: '/freibeuterhafen/hafenmeister',
        shipyard: '/freibeuterhafen/werft',
        fundworkshop: '/freibeuterhafen/fundwerkstatt',
      };

      if (routeMap[area.key]) {
        navigate(routeMap[area.key]);
        return;
      }
    }

    if (area.key === 'tavern') {
      navigate('/freibeuterhafen/taverne');
      return;
    }

    if (area.active && area.key === 'market') {
      marketSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (area.active && area.key === 'quests') {
      ordersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (area.active && area.key === 'fundworkshop') {
      navigate('/freibeuterhafen/fundwerkstatt');
      return;
    }

    if (area.active && area.key === 'shipyard') {
      navigate('/freibeuterhafen/werft');
      return;
    }

    if (area.active && area.key === 'harbormaster') {
      navigate('/freibeuterhafen/hafenmeister');
      return;
    }

    if (!area.active) {
      triggerLockShake(area.key);
      showNotice({
        type: 'info',
        icon: area.icon,
        title: `${area.label} öffnet bald`,
        badge: '🔒 Bereich noch gesperrt',
        lines: [area.text, 'Aktuell sind Markt und Aufträge geöffnet.'],
        rows: [
          { label: 'Status', value: 'Bald verfügbar' },
          { label: 'Im Hafen', value: area.label },
        ],
        footer: 'Der Freibeuterhafen wächst Schritt für Schritt weiter.',
      }, 4200);
    }
  };

  const handlePurchase = async (item) => {
    if (!item?.slug || purchaseState) return;

    setPurchaseState(item.slug);

    try {
      const walletBefore = wallet;
      const response = await fetch(shopPurchaseApiUrl || '/wp-json/pfandpirat/v1/shop/purchase', {
        method: 'POST',
        credentials: 'include',
        headers: getCsrfHeaders(nonce),
        body: JSON.stringify({ slug: item.slug, quantity: 1 }),
      });
      const payload = await readJsonResponse(response);

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Der Handel ist fehlgeschlagen.');
      }

      const nextWallet = payload?.wallet || walletBefore;
      const bronzeDelta = Math.max(0, getWalletBronze(walletBefore) - getWalletBronze(nextWallet));
      const effect = payload?.effect || {};
      const isShipyardResource = effect?.type === 'shipyard_resource' || item?.effect_type === 'shipyard_resource';
      const successLine = isShipyardResource
        ? (effect?.message || `+1 ${payload?.item?.title || item.title} für deine Werft.`)
        : 'Der Händler hat den Gegenstand in dein Inventar gelegt.';
      const successFooter = isShipyardResource
        ? 'Die Ressource wurde deiner Werft gutgeschrieben und zählt sofort für Schiff-Upgrades.'
        : 'Du kannst den Gegenstand direkt in deinem Inventar benutzen.';
      setWallet(nextWallet);
      if (payload?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(payload.session_patch);
      } else if (mergeSessionPatch) {
        mergeSessionPatch({ wallet: { wallet: nextWallet } });
      }
      triggerWalletPulse();
      triggerPurchaseFlash(item.slug);
      showNotice({
        type: 'success',
        icon: '🛍️',
        image: item.image,
        title: `Gekauft: ${payload?.item?.title || item.title}`,
        badge: '⚓ Handel erfolgreich',
        lines: [
          successLine,
        ],
        rows: [
          { label: 'Münzabzug', value: `-${payload?.item?.price_label || item.price_label}` },
          { label: 'Intern', value: `-${formatInternalBronze(bronzeDelta || item?.price_bronze || 0)}` },
          { label: 'Neuer Stand', value: formatWallet(nextWallet) },
        ],
        footer: successFooter,
      }, 4800);

      await loadShop();
    } catch (purchaseError) {
      showNotice({
        type: 'error',
        icon: '⚠️',
        title: 'Handel abgebrochen',
        badge: 'Kein Kauf ausgeführt',
        lines: [purchaseError.message || 'Dieses Angebot konnte gerade nicht gekauft werden.'],
      }, 4400);
    } finally {
      setPurchaseState(null);
    }
  };

  const handleAcceptOrder = async (order) => {
    if (!order?.id || orderActionId) return;
    setOrderActionId(`accept-${order.id}`);

    try {
      const payload = await acceptHarborOrder(order.id);
      const nextOrders = Array.isArray(payload?.orders) ? payload.orders : orders;
      const nextActiveOrder = payload?.active_order || null;
      setOrders(nextOrders);
      setActiveOrder(nextActiveOrder);
      if (mergeSessionPatch) {
        mergeSessionPatch({ harbor_orders: { orders: nextOrders, active_order: nextActiveOrder } });
      }
      window.dispatchEvent(new CustomEvent('pfandpirat:harbor-order-accepted', { detail: payload }));
      showNotice({
        type: 'success',
        icon: order.icon || '📜',
        title: `Auftrag angenommen: ${payload?.active_order?.title || order.title}`,
        badge: '📜 Auftrag aktiv',
        lines: ['Die alte Tagesaufgabe wird in der Übersicht jetzt durch diesen Auftrag ersetzt.'],
        rows: [
          { label: 'Ziel', value: payload?.active_order?.target_label || order.target_label },
          { label: 'Belohnung', value: formatOrderReward(payload?.active_order || order) },
        ],
        footer: 'Sammle jetzt Pfand-Einträge. Der Fortschritt wird serverseitig aus deinen echten Einträgen berechnet.',
      }, 5200);
      await loadOrders();
    } catch (acceptError) {
      const nextActive = acceptError?.payload?.data?.active_order || acceptError?.payload?.active_order;
      if (nextActive) setActiveOrder(nextActive);
      showNotice({
        type: 'error',
        icon: '⚠️',
        title: 'Auftrag konnte nicht angenommen werden',
        badge: 'Kein Auftrag gestartet',
        lines: [acceptError.message || 'Bitte schließe zuerst deinen aktiven Auftrag ab.'],
      }, 4400);
    } finally {
      setOrderActionId(null);
    }
  };

  const handleClaimOrder = async () => {
    if (!activeOrder?.can_claim || orderActionId) return;
    setOrderActionId('claim-active');

    try {
      const payload = await claimActiveHarborOrder();
      const nextActiveOrder = payload?.active_order || null;
      const nextOrders = Array.isArray(payload?.orders) ? payload.orders : orders;
      setActiveOrder(nextActiveOrder);
      setOrders(nextOrders);
      if (mergeSessionPatch) {
        mergeSessionPatch({ harbor_orders: { orders: nextOrders, active_order: nextActiveOrder }, wallet: payload?.wallet ? { wallet: payload.wallet } : undefined });
      }
      applyGamificationPayload?.(payload, {
        emitXpToast: true,
        event: payload?.event || 'harbor_order_claim',
        awarded: Number(payload?.awarded || 0),
        toastLabel: payload?.xp_toast?.label || `Auftrag: ${activeOrder.title}`,
      });
      window.dispatchEvent(new CustomEvent('pfandpirat:harbor-order-claimed', { detail: payload }));
      showNotice({
        type: 'success',
        icon: '🏆',
        title: 'Auftrag abgeschlossen',
        badge: 'Belohnung erhalten',
        lines: [payload?.message || 'Deine Belohnung wurde gutgeschrieben.'],
        rows: [
          { label: 'XP', value: `+${Number(payload?.awarded || 0)} XP` },
          { label: 'Münzen', value: payload?.claimed_order?.rewards?.bronze_label || activeOrder?.rewards?.bronze_label || 'gutgeschrieben' },
        ],
        footer: 'Du kannst jetzt im Hafen einen neuen Auftrag annehmen.',
      }, 5200);
      await loadOrders();
    } catch (claimError) {
      if (isNoActiveOrderError(claimError)) {
        setActiveOrder(null);
        showNotice({
          type: 'info',
          icon: '📜',
          title: 'Auftrag schon aktualisiert',
          badge: 'Kein aktiver Auftrag',
          lines: ['Der Auftrag ist nicht mehr offen. Das Auftragsbrett wird neu geladen.'],
        }, 3600);
        await loadOrders();
        return;
      }

      showNotice({
        type: 'error',
        icon: '⚠️',
        title: 'Belohnung konnte nicht abgeholt werden',
        badge: 'Auftrag nicht abgeschlossen',
        lines: [claimError.message || 'Bitte prüfe den Fortschritt erneut.'],
      }, 4400);
      await loadOrders();
    } finally {
      setOrderActionId(null);
    }
  };

  const canBuyItem = (item) => {
    if (loading || purchaseState) return false;
    if (item?.can_purchase === false) return false;
    if (item?.daily_remaining !== null && item?.daily_remaining !== undefined && Number(item.daily_remaining) <= 0) return false;
    if (item?.weekly_remaining !== null && item?.weekly_remaining !== undefined && Number(item.weekly_remaining) <= 0) return false;
    return true;
  };

  return (
    <div className="pfp-harbor-page">
      <Helmet>
        <title>Freibeuterhafen – Pfandpirat</title>
        <meta
          name="description"
          content="Der Freibeuterhafen ist der Pfandpirat-Markt für nützliche Ingame-Items wie Energydrink, Alte Flaschenpost, XP-Boost und aktive Aufträge."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="pfp-harbor-shell">
        <div className="pfp-harbor-navigation">
          <button type="button" className="pfp-harbor-back" onClick={() => navigate('/inventar')}>
            ← Zurück zum Inventar
          </button>
          <button type="button" className="pfp-harbor-set-sail" onClick={handleSetSail} title="Lege vor dem Hafen ab und kehre in die 3D-Seereise zurück.">
            Segel setzen
          </button>
        </div>

        <div className="pfp-harbor-hero-stage">
          <button
            type="button"
            className={`pfp-harbor-sound-toggle ${soundEnabled ? 'is-active' : ''}`}
            onClick={toggleHarborSound}
            aria-pressed={soundEnabled}
          >
            {soundEnabled ? '🔊 Hafensound läuft' : '🔈 Hafensound starten'}
          </button>
          <section className="pfp-harbor-hero" aria-label="Freibeuterhafen">
            <img
              className="pfp-harbor-hero-poster"
              src={harborImage}
              alt="Freibeuterhafen mit Markt, Taverne, Hafenmeister, Werft, Schatzkisten und Aufträgen"
            />
            {!prefersReducedMotion && !videoFailed ? (
              <video
                className="pfp-harbor-video"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={harborImage}
                onError={() => setVideoFailed(true)}
              >
                <source src={HARBOR_VIDEO_URL} type="video/mp4" />
              </video>
            ) : (
              null
            )}

            {HARBOR_AREAS.map((area) => (
              <button
                key={area.key}
                type="button"
                className={`pfp-harbor-hotspot ${area.key === 'fundworkshop' ? 'pfp-harbor-hotspot--sign' : ''} ${area.active ? 'is-active' : 'is-locked'} ${activeArea === area.key ? 'is-selected' : ''} ${shakenArea === area.key ? 'is-shaking' : ''}`}
                style={{ left: `${area.x}%`, top: `${area.y}%` }}
                onClick={() => handleAreaClick(area)}
                aria-label={`${area.label}${area.active ? '' : ' bald verfügbar'}`}
              >
                {area.key === 'fundworkshop' ? (
                  <>
                    <img src={fundWorkshopSign} alt="" aria-hidden="true" />
                    <span className="pfp-harbor-sign-label"><b>Zur</b><b>Fundwerkstatt</b></span>
                  </>
                ) : (
                  <>
                    <span className="pfp-harbor-hotspot-icon">{area.icon}</span>
                    <strong>{area.label}</strong>
                    <small className="pfp-harbor-hotspot-state">{area.stateLabel || (area.active ? 'aktiv' : '🔒 bald')}</small>
                  </>
                )}
              </button>
            ))}
          </section>
        </div>

        <section className="pfp-harbor-area-list" aria-label="Hafenbereiche">
          {HARBOR_AREAS.map((area) => (
            <button
              key={area.key}
              type="button"
              className={`${area.active ? 'is-active' : 'is-locked'} ${activeArea === area.key ? 'is-selected' : ''} ${shakenArea === area.key ? 'is-shaking' : ''}`}
              onClick={() => handleAreaClick(area)}
            >
              <span>{area.icon}</span>
              <strong>{area.label}</strong>
              <em>{area.stateLabel || (area.active ? 'aktiv' : '🔒 bald')}</em>
            </button>
          ))}
        </section>

        <section ref={marketSectionRef} className="pfp-harbor-market" aria-label="Markt im Freibeuterhafen">
          <div className="pfp-harbor-market-head">
            <div>
              <h1>Markt der Freibeuter</h1>
              <p>
                Tausche deine Pfandpirat-Münzen gegen Items mit direktem Nutzen im Game.
                Kosmetik, Crew-Vorteile und echte Spezialaktionen bleiben für spätere Hafenausbauten reserviert.
              </p>
            </div>
            <div className={`pfp-harbor-wallet ${walletPulse ? 'is-pulsing' : ''}`} aria-live="polite">
              <span>Dein Münzbeutel</span>
              <strong>{formatWallet(wallet)}</strong>
              <em>intern sauber als Bronzewert im Ledger geführt</em>
            </div>
          </div>

          {loading && (
            <div className="pfp-harbor-loading" role="status" aria-live="polite">
              <span />
              <strong>Der Hafenmeister zählt deine Münzen …</strong>
            </div>
          )}

          {error && (
            <div className="pfp-harbor-error">
              <strong>Der Markt ist gerade schwer erreichbar.</strong>
              <span>{error.message}</span>
              <button type="button" onClick={loadShop}>Erneut versuchen</button>
            </div>
          )}

          <div className="pfp-harbor-product-grid">
            {marketItems.map((item) => {
              const buying = purchaseState === item.slug;
              const enabled = canBuyItem(item);
              const imageFailed = Boolean(failedItemImages[item.slug]);

              return (
                <article key={item.slug} className={`pfp-harbor-product-card ${purchaseFlashSlug === item.slug ? 'is-flashing' : ''}`}>
                  <div className="pfp-harbor-product-ribbon">{item.tag}</div>
                  <div className="pfp-harbor-product-coin">☠</div>
                  <div className="pfp-harbor-product-image">
                    {item.image && !imageFailed ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        onError={() => setFailedItemImages((previous) => ({ ...previous, [item.slug]: true }))}
                      />
                    ) : (
                      <span>{item.icon}</span>
                    )}
                  </div>
                  <div className="pfp-harbor-product-body">
                    <h2>{item.title}</h2>
                    <p>{item.description}</p>
                    <div className="pfp-harbor-product-meta">
                      <span>{limitLabel(item)}</span>
                    </div>
                  </div>
                  <div className="pfp-harbor-product-buybar">
                    <div className="pfp-harbor-price">
                      <small>Preis</small>
                      <strong>{item.price_label}</strong>
                    </div>
                    <button
                      type="button"
                      className={purchaseFlashSlug === item.slug ? 'is-gold-flash' : ''}
                      onClick={() => handlePurchase(item)}
                      disabled={!enabled || buying}
                    >
                      {buying ? 'Handel läuft …' : enabled ? 'Kaufen' : 'Nicht verfügbar'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section ref={ordersSectionRef} className="pfp-harbor-orders" aria-label="Aufträge im Freibeuterhafen">
          <div className="pfp-harbor-orders-head">
            <div>
              <h2>Auftragsbrett der Freibeuter</h2>
              <p>
                Wähle genau einen aktiven Auftrag. Sobald ein Auftrag aktiv ist, ersetzt er die alte Tagesaufgabe in der Übersicht.
                Ohne aktiven Auftrag bleibt die bisherige Tagesaufgabe als Fallback sichtbar.
              </p>
            </div>
            {activeOrder ? (
              <div className="pfp-harbor-active-order-mini">
                <span>{activeOrder.icon || '📜'} Aktiver Auftrag</span>
                <strong>{activeOrder.title}</strong>
                <em>{formatOrderProgress(activeOrder)}</em>
                {activeOrder.can_claim ? (
                  <button type="button" onClick={handleClaimOrder} disabled={orderActionId === 'claim-active'}>
                    {orderActionId === 'claim-active' ? 'Hole Belohnung …' : 'Belohnung abholen'}
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="pfp-harbor-active-order-mini is-empty">
                <span>📌 Kein Auftrag aktiv</span>
                <strong>Wähle unten einen Auftrag</strong>
                <em>Dann richtet sich die Übersicht danach.</em>
              </div>
            )}
          </div>

          {ordersLoading ? (
            <div className="pfp-harbor-loading" role="status" aria-live="polite">
              <span />
              <strong>Das Auftragsbrett wird ausgerollt …</strong>
            </div>
          ) : null}

          {ordersError ? (
            <div className="pfp-harbor-error">
              <strong>Die Aufträge sind gerade schwer erreichbar.</strong>
              <span>{ordersError.message}</span>
              <button type="button" onClick={loadOrders}>Erneut versuchen</button>
            </div>
          ) : null}

          <div className="pfp-harbor-order-grid">
            {orders.map((order) => {
              const isActive = activeOrder?.id === order.id;
              const progress = isActive ? activeOrder?.progress : order?.progress;
              const percent = clampPercent(progress?.percent || 0);
              const accepting = orderActionId === `accept-${order.id}`;
              const canAccept = Boolean(order.can_accept) && !activeOrder;

              const complete = isActive
                ? Boolean(activeOrder?.can_claim || progress?.is_complete)
                : Boolean(order?.progress?.is_complete);

              return (
                <article key={order.id || order.slug} className={`pfp-harbor-order-card ${isActive ? 'is-active' : ''} ${complete ? 'is-complete' : ''}`}>
                  <div className="pfp-harbor-order-card__top">
                    <span className="pfp-harbor-order-card__icon">{order.icon || '📜'}</span>
                    <div>
                      <strong>{order.title}</strong>
                      <em>{order.difficulty || 'normal'}</em>
                    </div>
                  </div>
                  <p>{order.description}</p>
                  <div className="pfp-harbor-order-card__facts">
                    <span>Ziel: <strong>{order.target_label}</strong></span>
                    <span>Belohnung: <strong>{formatOrderReward(order)}</strong></span>
                  </div>
                  {isActive ? (
                    <>
                      <div className="pfp-harbor-order-card__bar"><span style={{ width: `${percent}%` }} /></div>
                      <div className="pfp-harbor-order-card__progress">{formatOrderProgress(activeOrder)}</div>
                    </>
                  ) : null}
                  <div className="pfp-harbor-order-card__actions">
                    {isActive && activeOrder?.can_claim ? (
                      <button type="button" className="is-primary" onClick={handleClaimOrder} disabled={orderActionId === 'claim-active'}>
                        {orderActionId === 'claim-active' ? 'Hole Belohnung …' : 'Belohnung abholen'}
                      </button>
                    ) : isActive ? (
                      <button type="button" onClick={loadOrders} disabled={ordersLoading}>Fortschritt prüfen</button>
                    ) : (
                      <button type="button" onClick={() => handleAcceptOrder(order)} disabled={!canAccept || accepting}>
                        {accepting ? 'Wird angenommen …' : canAccept ? 'Auftrag annehmen' : 'Gesperrt solange Auftrag aktiv'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>

      {notice && (
        <div className="pfp-harbor-modal-backdrop" role="button" tabIndex={0} onClick={closeNotice} onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            closeNotice();
          }
        }}>
          <div className={`pfp-harbor-modal is-${notice.type || 'info'}`} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="pfp-harbor-modal-close" onClick={closeNotice} aria-label="Fenster schließen">×</button>
            <div className="pfp-harbor-modal-icon">
              {notice.image ? <img src={notice.image} alt="" aria-hidden="true" /> : <span>{notice.icon || '⚓'}</span>}
            </div>
            {notice.type === 'success' && (
              <div className="pfp-harbor-coin-particles" aria-hidden="true">
                <span>●</span><span>●</span><span>●</span><span>●</span><span>●</span><span>●</span>
              </div>
            )}
            {notice.badge && <div className="pfp-harbor-modal-badge">{notice.badge}</div>}
            <h2>{notice.title}</h2>
            {notice.lines?.map((line, index) => <p key={`${notice.title}-${index}`}>{line}</p>)}
            {Array.isArray(notice.rows) && notice.rows.length > 0 && (
              <div className="pfp-harbor-modal-summary" aria-label="Zusammenfassung">
                {notice.rows.map((row) => (
                  <div key={`${notice.title}-${row.label}`} className="pfp-harbor-modal-summary-row">
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            )}
            {notice.footer && <small>{notice.footer}</small>}
          </div>
        </div>
      )}
    </div>
  );
}
